import type {AriaTabPanelProps} from '@react-types/tabs';
import type {DOMAttributes} from '@react-types/shared';
import type {RefObject} from 'react';
import {mergeProps, useLabels} from '../utils';
import {useHasTabbableChild} from '../focus';
import type {TabListState} from '../../@react-stately/tabs';

import {generateId} from './utils';

export interface TabPanelAria {
  /** Props for the tab panel element. */
  tabPanelProps: DOMAttributes;
}

/**
 * Provides the behavior and accessibility implementation for a tab panel. A tab panel is a container for
 * the contents of a tab, and is shown when the tab is selected.
 */
export function useTabPanel<T>(
  props: AriaTabPanelProps,
  state: TabListState<T>,
  ref: RefObject<Element>,
): TabPanelAria {
  // The tabpanel should have tabIndex=0 when there are no tabbable elements within it.
  // Otherwise, tabbing from the focused tab should go directly to the first tabbable element
  // within the tabpanel.
  const tabIndex = useHasTabbableChild(ref) ? undefined : 0;
  const id = generateId(state, props.id ?? state?.selectedKey, 'tabpanel');
  const tabPanelProps = useLabels({
    ...props,
    id,
    'aria-labelledby': generateId(state, state?.selectedKey, 'tab'),
  });

  return {
    tabPanelProps: mergeProps(tabPanelProps, {
      tabIndex,
      role: 'tabpanel',
      'aria-describedby': props['aria-describedby'],
      'aria-details': props['aria-details'],
    }),
  };
}
